/*
 * Renderer: timepicker / timerange
 *   type="timepicker"  – single time value (HH:MM) with an editable text field
 *   type="timerange"   – a "Von / Bis" (from/to) time pair in a two-column dropdown
 *
 * The user can type a time directly (timepicker only) or scroll through hour and minute
 * columns in the dropdown panel to pick a value.
 *
 * timepicker: fires `input-change` with detail { value: "HH:MM" }.
 *             Use getValue() / setValue("HH:MM" | null).
 * timerange:  fires `input-change` with detail { from: "HH:MM", to: "HH:MM" }.
 *             Use getValue() / setValue({ from, to }).
 *
 * Attributes:
 *   min-time     – earliest selectable time (HH:MM); earlier slots are disabled
 *   max-time     – latest selectable time (HH:MM); later slots are disabled
 *   minute-step  – only show multiples of N minutes in the picker (default 30)
 *   strict-range – remove out-of-range slots entirely instead of showing them disabled
 *   placeholder  – shown in the timepicker text field when empty (default "--:--")
 *   disabled     – disables all interaction
 */
export const TimerangeRenderer = {

    renderTimepicker() {
        this.clearShadow();

        const wrap = document.createElement('div');
        wrap.className = 'inputEditableTrigger';
        if (this.hasAttribute('disabled')) wrap.classList.add('disabled');

        const input = document.createElement('input');
        input.type        = 'text';
        input.className   = 'inputEditableInput';
        input.placeholder = this.getAttribute('placeholder') || '--:--';
        input.maxLength   = 5;
        input.value       = this.timeValue || '';
        input.disabled    = this.hasAttribute('disabled');

        const icon = document.createElement('i');
        icon.className = 'fa-solid fa-clock inputEditableIcon';
        icon.setAttribute('aria-hidden', 'true');

        wrap.appendChild(input);
        wrap.appendChild(icon);

        const panel = document.createElement('div');
        panel.className = 'inputPanel timerangePanel';
        panel.hidden    = true;

        this.trigger   = wrap;
        this.panel     = panel;
        this.timeInput = input;
        this.buildTimepickerPanel();

        input.addEventListener('input', () => {
            const cursor = input.selectionStart;
            const prev   = input.value;

            let val = prev.replace(/[^\d:]/g, '').slice(0, 5);

            if (!val.includes(':') && val.length >= 3) {
                val = val.slice(0, 2) + ':' + val.slice(2, 4);
            }

            if (val !== prev) {
                input.value = val;
                const adj = val.length > prev.length ? 1 : 0;
                input.setSelectionRange(cursor + adj, cursor + adj);
            }

            const match = val.match(/^(\d{1,2}):(\d{2})$/);
            if (!match) {
                if (!val) {
                    this.timeValue = null;
                    this.dispatchEvent(new CustomEvent('input-change', {
                        bubbles: true, composed: true, detail: { value: null },
                    }));
                }
                return;
            }
            const hours = parseInt(match[1], 10);
            const minutes = parseInt(match[2], 10);
            if (hours < 0 || hours > 23 || minutes < 0 || minutes >= 60) return;
            const formatted = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
            this.timeValue = formatted;
            if (this.panel && !this.panel.hidden) this.buildTimepickerPanel();
            this.dispatchEvent(new CustomEvent('input-change', {
                bubbles: true, composed: true, detail: { value: formatted },
            }));
        });

        wrap.addEventListener('click', e => {
            if (e.target === input) return;
            e.stopPropagation();
            this.togglePanel();
        });

        input.addEventListener('keydown', e => {
            if (e.key === 'Escape')    this.closePanel();
            if (e.key === 'ArrowDown') { e.preventDefault(); this.openPanel(); }
        });

        this.shadowRoot.appendChild(wrap);
        this.shadowRoot.appendChild(panel);
        this.attachOutsideHandler();
    },

    buildTimepickerPanel() {
        const panel = this.panel;
        if (!panel) return;
        panel.innerHTML = '';

        panel.appendChild(this.buildTimePicker('', this.timeValue, val => {
            this.timeValue = val;
            this.updateTimepickerTrigger();
            this.dispatchEvent(new CustomEvent('input-change', {
                bubbles: true, composed: true,
                detail: { value: val },
            }));
        }));
    },

    updateTimepickerTrigger() {
        if (this.timeInput) this.timeInput.value = this.timeValue ?? '';
    },

    renderTimerange() {
        this.clearShadow();

        const trigger = this.buildTrigger(`${this.timeFrom} - ${this.timeTo}`, 'clock');
        const panel   = document.createElement('div');
        panel.className = 'inputPanel timerangePanel';
        panel.hidden    = true;

        this.trigger = trigger;
        this.panel   = panel;
        this.buildTimerangePanel();

        trigger.addEventListener('click', e => { e.stopPropagation(); this.togglePanel(); });
        trigger.addEventListener('keydown', e => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.togglePanel(); }
            if (e.key === 'Escape') this.closePanel();
            if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !this.isOpen) {
                e.preventDefault();
                this.openPanel();
            }
        });

        this.shadowRoot.appendChild(trigger);
        this.shadowRoot.appendChild(panel);
        this.attachOutsideHandler();
    },

    buildTimerangePanel() {
        const panel = this.panel;
        if (!panel) return;
        panel.innerHTML = '';

        panel.appendChild(this.buildTimePicker('Von', this.timeFrom, val => {
            this.timeFrom = val;
            this.updateTimerangeTrigger();
            this.dispatchTimerange();
        }));

        const divider = document.createElement('div');
        divider.className = 'timeDivider';
        panel.appendChild(divider);

        panel.appendChild(this.buildTimePicker('Bis', this.timeTo, val => {
            this.timeTo = val;
            this.updateTimerangeTrigger();
            this.dispatchTimerange();
        }));
    },

    updateTimerangeTrigger() {
        const lbl = this.trigger?.querySelector('.inputTriggerLabel');
        if (lbl) lbl.textContent = `${this.timeFrom} - ${this.timeTo}`;
    },

    dispatchTimerange() {
        this.dispatchEvent(new CustomEvent('input-change', {
            bubbles: true, composed: true,
            detail: { from: this.timeFrom, to: this.timeTo },
        }));
    },

    buildTimePicker(labelText, currentValue, onChange) {
        const [curH, curM] = (currentValue || '00:00').split(':').map(Number);

        const step    = Math.max(1, parseInt(this.getAttribute('minute-step') || '30', 10));
        const MINUTES = Array.from({ length: Math.ceil(60 / step) }, (_, i) => i * step)
                             .filter(minute => minute < 60);

        const minParts = parseTime(this.getAttribute('min-time'));
        const maxParts = parseTime(this.getAttribute('max-time'));
        const minH = minParts ? minParts[0] : 0;
        const minM = minParts ? minParts[1] : 0;
        const maxH = maxParts ? maxParts[0] : 23;
        const maxM = maxParts ? maxParts[1] : 59;
        const strictRange = this.hasAttribute('strict-range');

        const warnParts = parseTime(this.getAttribute('warn-time'));
        const warnH = warnParts ? warnParts[0] : null;
        const warnM = warnParts ? warnParts[1] : 0;

        const isHourDisabled = hour => hour < minH || hour > maxH;
        const isMinDisabled  = (hour, minute) => (hour === minH && minute < minM) || (hour === maxH && minute > maxM);
        const isHourWarned   = hour => warnH !== null && hour > warnH;
        const isMinWarned    = (hour, minute) => warnH !== null && (hour > warnH || (hour === warnH && minute > warnM));

        const wrap = document.createElement('div');
        wrap.className = 'timePicker';

        if (labelText) {
            const lbl = document.createElement('div');
            lbl.className   = 'timePickerLabel';
            lbl.textContent = labelText;
            wrap.appendChild(lbl);
        }

        const cols    = document.createElement('div');
        cols.className = 'timePickerCols';

        const hourCol = document.createElement('div');
        hourCol.className = 'timeScrollCol';

        const minCol  = document.createElement('div');
        minCol.className = 'timeScrollCol';

        const buildMinCol = (hour, preferMinute = -1) => {
            minCol.innerHTML = '';
            MINUTES.forEach(minute => {
                const dis = this.hasAttribute('disabled') || isMinDisabled(hour, minute);
                if (strictRange && dis) return;

                const item = document.createElement('div');
                item.className   = 'timeScrollItem' + (!strictRange && dis ? ' disabled' : '') + (!dis && isMinWarned(hour, minute) ? ' warned' : '');
                item.textContent = String(minute).padStart(2, '0');
                item.dataset.m   = minute;
                item.setAttribute('tabindex', '-1');
                item.setAttribute('role', 'option');
                item.setAttribute('aria-selected', 'false');
                if (!strictRange && dis) item.setAttribute('aria-disabled', 'true');

                if (!dis) {
                    item.addEventListener('click', e => {
                        e.stopPropagation();
                        selectMinute(minCol, hourCol, item, minute, onChange);
                    });
                    item.addEventListener('keydown', e => handleTimeKeydown(
                        e, minCol, item,
                        () => selectMinute(minCol, hourCol, item, minute, onChange),
                        null,
                        () => { this.closePanel(); this.trigger?.focus(); }
                    ));
                }
                minCol.appendChild(item);
            });

            const allItems = [...minCol.querySelectorAll('.timeScrollItem:not(.disabled)')];
            let sel = preferMinute >= 0 ? allItems.find(i => parseInt(i.dataset.m) === preferMinute) : null;
            if (!sel) sel = allItems[0];
            if (sel) {
                sel.classList.add('selected');
                sel.setAttribute('tabindex', '0');
                sel.setAttribute('aria-selected', 'true');
            }
        };

        for (let hour = 0; hour <= 23; hour++) {
            const dis = this.hasAttribute('disabled') || isHourDisabled(hour);
            if (strictRange && dis) continue;

            const item = document.createElement('div');
            item.className   = 'timeScrollItem' + (hour === curH ? ' selected' : '') + (!strictRange && dis ? ' disabled' : '') + (!dis && isHourWarned(hour) ? ' warned' : '');
            item.textContent = String(hour).padStart(2, '0');
            item.dataset.h   = hour;
            item.setAttribute('tabindex', !dis && hour === curH ? '0' : '-1');
            item.setAttribute('role', 'option');
            item.setAttribute('aria-selected', String(hour === curH));
            if (!strictRange && dis) item.setAttribute('aria-disabled', 'true');

            if (!dis) {
                const doSelectHour = () => {
                    if (strictRange) {
                        hourCol.querySelectorAll('.timeScrollItem').forEach(i => {
                            i.classList.remove('selected');
                            i.setAttribute('tabindex', '-1');
                            i.setAttribute('aria-selected', 'false');
                        });
                        item.classList.add('selected');
                        item.setAttribute('tabindex', '0');
                        item.setAttribute('aria-selected', 'true');

                        const prevMinute  = parseInt(minCol.querySelector('.timeScrollItem.selected')?.dataset.m ?? -1, 10);
                        buildMinCol(hour, prevMinute);

                        const finalMinute = parseInt(minCol.querySelector('.timeScrollItem.selected')?.dataset.m ?? 0, 10);
                        onChange(`${String(hour).padStart(2, '0')}:${String(finalMinute).padStart(2, '0')}`);
                        requestAnimationFrame(() => scrollToSelected(minCol));
                    } else {
                        selectHour(hourCol, minCol, item, hour, onChange, isMinDisabled);
                    }
                };
                item.addEventListener('click', e => { e.stopPropagation(); doSelectHour(); });
                item.addEventListener('keydown', e => handleTimeKeydown(
                    e, hourCol, item,
                    doSelectHour,
                    minCol,
                    () => { this.closePanel(); this.trigger?.focus(); }
                ));
            }
            hourCol.appendChild(item);
        }

        buildMinCol(curH, curM);

        const sep = document.createElement('div');
        sep.className   = 'timeColSep';
        sep.textContent = ':';
        sep.setAttribute('aria-hidden', 'true');

        cols.appendChild(hourCol);
        cols.appendChild(sep);
        cols.appendChild(minCol);
        wrap.appendChild(cols);

        requestAnimationFrame(() => {
            scrollToSelected(hourCol);
            scrollToSelected(minCol);
        });

        return wrap;
    },
};

function parseTime(str) {
    if (!str) return null;
    const parts = str.split(':').map(Number);
    return (parts.length === 2 && parts.every(num => !isNaN(num))) ? parts : null;
}

function scrollToSelected(col) {
    const sel = col.querySelector('.timeScrollItem.selected');
    if (!sel) return;
    col.scrollTop = sel.offsetTop - col.clientHeight / 2 + sel.offsetHeight / 2;
}

function selectHour(hourCol, minCol, item, hour, onChange, isMinDisabled) {
    hourCol.querySelectorAll('.timeScrollItem').forEach(i => {
        i.classList.remove('selected');
        i.setAttribute('tabindex', '-1');
        i.setAttribute('aria-selected', 'false');
    });
    item.classList.add('selected');
    item.setAttribute('tabindex', '0');
    item.setAttribute('aria-selected', 'true');

    let selMinItem = null;
    minCol.querySelectorAll('.timeScrollItem').forEach(minuteItem => {
        const minute = parseInt(minuteItem.dataset.m, 10);
        const dis = isMinDisabled(hour, minute);
        minuteItem.classList.toggle('disabled', dis);
        minuteItem.setAttribute('aria-disabled', String(dis));
        if (minuteItem.classList.contains('selected')) selMinItem = minuteItem;
    });

    if (selMinItem?.classList.contains('disabled')) {
        const first = [...minCol.querySelectorAll('.timeScrollItem')]
            .find(i => !i.classList.contains('disabled'));
        if (first) {
            selMinItem.classList.remove('selected');
            selMinItem.setAttribute('tabindex', '-1');
            selMinItem.setAttribute('aria-selected', 'false');
            first.classList.add('selected');
            first.setAttribute('tabindex', '0');
            first.setAttribute('aria-selected', 'true');
        }
    }

    const selMinute = parseInt(minCol.querySelector('.timeScrollItem.selected')?.dataset.m ?? 0, 10);
    onChange(`${String(hour).padStart(2, '0')}:${String(selMinute).padStart(2, '0')}`);
}

function selectMinute(minCol, hourCol, item, minute, onChange) {
    minCol.querySelectorAll('.timeScrollItem').forEach(i => {
        i.classList.remove('selected');
        i.setAttribute('tabindex', '-1');
        i.setAttribute('aria-selected', 'false');
    });
    item.classList.add('selected');
    item.setAttribute('tabindex', '0');
    item.setAttribute('aria-selected', 'true');

    const selHour = parseInt(hourCol.querySelector('.timeScrollItem.selected')?.dataset.h ?? 0, 10);
    onChange(`${String(selHour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
}

function handleTimeKeydown(e, col, item, onSelect, nextCol, onEscape) {
    switch (e.key) {
        case 'Enter':
        case ' ':
            e.preventDefault();
            e.stopPropagation();
            onSelect();
            break;
        case 'Escape':
            e.stopPropagation();
            onEscape();
            break;
        case 'ArrowUp': {
            e.preventDefault();
            e.stopPropagation();
            const prev = prevEnabled(col, item);
            if (prev) prev.focus();
            break;
        }
        case 'ArrowDown': {
            e.preventDefault();
            e.stopPropagation();
            const next = nextEnabled(col, item);
            if (next) next.focus();
            break;
        }
        case 'Tab':
            if (!e.shiftKey && nextCol) {
                e.preventDefault();
                e.stopPropagation();
                (nextCol.querySelector('.timeScrollItem.selected:not(.disabled)') ||
                 nextCol.querySelector('.timeScrollItem:not(.disabled)'))?.focus();
            }
            break;
    }
}

function prevEnabled(col, item) {
    let el = item.previousElementSibling;
    while (el) {
        if (!el.classList.contains('disabled')) return el;
        el = el.previousElementSibling;
    }
    return null;
}

function nextEnabled(col, item) {
    let el = item.nextElementSibling;
    while (el) {
        if (!el.classList.contains('disabled')) return el;
        el = el.nextElementSibling;
    }
    return null;
}
